import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, first, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import {
    clearConfirmation,
    closeDialog,
    closeSiltDialog,
    collectLocation,
    CoreActionType,
    exportXml,
    FormActionType,
    handleConfirmationResult,
    loadModemProviders,
    openLocationDashboard,
    openModemProviderDialog,
    openSiltDialog,
    pickMapCoords,
    scheduleCollect,
    setMapCoords,
    submitForm,
} from '../actions';
import { LocationCardState } from '../reducers';
import { Action, Store } from '@ngrx/store';
import { ModemSetupComponent } from '../../../map/modem-setup';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmationType, LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { activeInactiveLocationQueryParam, customerLocationGroupQueryParam } from 'app/shared/models/customer';
import { MapService } from 'app/shared/services/map.service';
import { GISService } from 'app/shared/services/gis-service';
import { ActivatedRoute, Router } from '@angular/router';
import { CollectService } from 'app/shared/services/collect.service';
import { StatusCodeService } from 'app/shared/services/status-code.service';
import { ScheduleCollectionService } from 'app/shared/services/schedule-collection.service';
import { LocationService } from 'app/shared/services/location.service';
import { LocationDetails } from 'app/shared/models/location-details';
import { LocationStatus } from 'app/shared/models/locations';
import { CollectData, DataCollectionDialogResult } from 'app/shared/models/monitorConfiguration';
import { DateutilService } from 'app/shared/services/dateutil.service';
import { DataCollectionComponent } from 'app/pages/data-collection/data-collection.component';
import { HttpErrorResponse } from '@angular/common/http';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AdsPrismAddScheduleComponent } from 'app/pages/dashboards/schedule-collect-dashboard';
import { UiService } from 'app/shared/services/ui.service';
import { UsersService } from 'app/pages/admin/users.service';
import { SiltDialogCloseDialog, SiltEditorComponent, SiltEditorDialogData } from 'app/pages/silt-editor/silt-editor.component';

const scheduleCollectDashboardURL = '/pages/menuDashboard/schedule-collect/overview';

interface ScheduleDialogData {
    customerId: number;
    locationId: number;
    isEditMode: boolean;
    selectedScheduleId?: number;
}

@Injectable()
export class UIEffects {
    private actions$: Actions<Action> = inject(Actions);

    private store: Store<{ locationCard: LocationCardState<unknown> }> = inject(Store);
    private dialog = inject(MatDialog);
    private gisService = inject(GISService);
    private mapService = inject(MapService);

    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);

    private collectService = inject(CollectService);
    private statusCodeService = inject(StatusCodeService);

    private scheduleCollectionService = inject(ScheduleCollectionService);
    private locationService = inject(LocationService);
    private dateUtilService = inject(DateutilService);
    private snackBar = inject(MatSnackBar);
    private translateService = inject(TranslateService);

    private usersService = inject(UsersService);
    private uiSerivce = inject(UiService);

    constructor() {}

    handleConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(handleConfirmationResult),
            withLatestFrom(this.store),
            switchMap(([{ result }, { locationCard }]) => {
                if (locationCard.permissions.isMonitorEditor) {
                    const { confirmation } = locationCard;
                    this.handleConfirmationResult(result, confirmation.action, locationCard);
                }

                return of(clearConfirmation());
            }),
        ),
    );

    closeDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(closeDialog),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    if (state.locationCard.pristine || !state.locationCard.permissions.isMonitorEditor) {
                        this.closeDialog();
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    pickMapCoords$ = createEffect(() =>
        this.actions$.pipe(
            ofType(pickMapCoords),
            withLatestFrom(this.store),
            filter(([_, { locationCard }]) => locationCard.permissions.isMonitorEditor),
            tap(() => this.uiSerivce.enableMap()),
            switchMap(() => this.gisService.coordinatePickerFromMap.pipe(first())),
            tap(() => this.uiSerivce.disableMap()),
            map((coords: { lat: number; lon: number }) => setMapCoords({ coords })),
        ),
    );

    openModemProvider$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openModemProviderDialog),
            withLatestFrom(this.store),
            filter(([_, { locationCard }]) => locationCard.permissions.isMonitorEditor),
            switchMap(() =>
                this.dialog.open(ModemSetupComponent, { disableClose: true, data: '' }).afterClosed().pipe(first()),
            ),
            map(() => loadModemProviders()),
        ),
    );

    openLocationDashboard$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openLocationDashboard),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    const { locationCard } = state;
                    if (locationCard.permissions.isMonitorEditor && locationCard.pristine) {
                        this.closeDialog(false);
                        this.openLocationDashboard(
                            locationCard.customer.customerID,
                            locationCard.locationDetails[0].locationID,
                        );
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    collectLocation$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(collectLocation),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    const { locationCard } = state;

                    if (locationCard.permissions.isMonitorEditor && locationCard.pristine) {
                        this.onCollectLocation(
                            locationCard.customer.customerID,
                            locationCard.locationDetails[0].locationID,
                        );
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    scheduleCollect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(scheduleCollect),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    const { locationCard } = state;
                    if (locationCard.permissions.isMonitorEditor && locationCard.pristine) {
                        this.navigateToScheduleCollectDashboard(
                            locationCard.customer.customerID,
                            locationCard.locationDetails[0].locationID,
                        );
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    exportXml$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(exportXml),
                withLatestFrom(this.store),
                switchMap(([action, state]) => {
                    const { locationCard } = state;
                    if (locationCard.permissions.isMonitorEditor && locationCard.pristine) {
                        this.exportLocationXml(
                            locationCard.customer.customerID,
                            locationCard.locationDetails[0].locationID,
                        );
                    }

                    return of(null);
                }),
            ),
        { dispatch: false },
    );

    openSiltEditDialog$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(openSiltDialog),
                withLatestFrom(this.store),
                filter(([_, { locationCard }]) => locationCard.permissions.isMonitorEditor),
                switchMap(([action, state]) => {
                    const { locationCard } = state;
                    const isFlexMonitor = locationCard.locationDetails[0].series === 'FLEX' ;
                    const isMetric = locationCard.settings.isMetric;
                    const mpDetail = locationCard.locationDetails[action.mpIndex] ?? locationCard.locationDetails[0];
                    const isLocationInactive =
                        mpDetail.status === LocationStatus.Inactive || mpDetail.status === LocationStatus.InactiveRemoved;

                    const data: SiltEditorDialogData = {
                        cid: locationCard?.locationCardData?.customerId,
                        lid: mpDetail?.locationID,
                        user: this.usersService.userName,
                        locationName: mpDetail?.locationName,
                        isFlexMonitor,
                        isMetric,
                        isLocationInactive,
                    };


                    const dialogRef = this.dialog.open(SiltEditorComponent, {
                        disableClose: true,
                        data,
                    });

                    return dialogRef.afterClosed().pipe(map((result: SiltDialogCloseDialog) => ({ updateFieldId: action.updateFieldId, result })));
                }),
                tap(({ updateFieldId, result }) => {
                    if (result && result.updated) {
                        this.store.dispatch(
                            closeSiltDialog({
                                updateFieldId,
                                lastEditedValue: Number(result.lastValue.editedValue),
                            }),
                        );
                    }
                })
            ),
        { dispatch: false },
    );

    // #49696 Ignore this parameter for now.
    private closeDialog(result?: boolean) {
        this.mapService.closeDialog(true);
    }

    private handleConfirmationResult(result: boolean, action: ConfirmationType, state: LocationCardState<unknown>) {
        if (result) {
            switch (action) {
                case ConfirmationType.Close: {
                    this.closeDialog(false);
                    break;
                }
                case ConfirmationType.LocationDashboard: {
                    this.closeDialog(false);
                    this.openLocationDashboard(state.customer.customerID, state.locationDetails[0].locationID);
                    break;
                }
                case ConfirmationType.DataCollection: {
                    this.onCollectLocation(state.customer.customerID, state.locationDetails[0].locationID);
                    break;
                }
                case ConfirmationType.ScheduleCollection: {
                    this.navigateToScheduleCollectDashboard(
                        state.customer.customerID,
                        state.locationDetails[0].locationID,
                    );
                    break;
                }
                case ConfirmationType.LocationExport: {
                    this.store.dispatch(submitForm({ shouldClose: false, isActivationRequired: false }));

                    break;
                }
            }
        } else {
            if (action === ConfirmationType.LocationExport) {
                this.exportLocationXml(state.customer.customerID, state.locationDetails[0].locationID);
            }
        }
    }

    public openLocationDashboard(customerId: number, locationId: number) {
        const locationGroupId = Number(this.activatedRoute.snapshot.queryParamMap.get(customerLocationGroupQueryParam));
        const includeActiveLocation = Number(this.activatedRoute.snapshot.queryParamMap.get(activeInactiveLocationQueryParam));

        this.router.navigate(['/pages/menuDashboard/viewData'], {
            queryParams: {
                c: customerId,
                lg: locationGroupId,
                lid: locationId,
                lt: includeActiveLocation,
            },
            relativeTo: this.activatedRoute,
        });
    }

    public onCollectLocation(customerId: number, locationId: number): void {
        this.locationService.getLocationDetails(customerId, locationId).subscribe((result: LocationDetails) => {
            if (!result) return;

            const lastCollectDate = this.calculateLastCollectDate(result);
            this.openDataCollectDialog(customerId, locationId, result, lastCollectDate);
        });
    }

    private calculateLastCollectDate(locationDetails: LocationDetails): Date | string {
        const dateFormat = this.dateUtilService.dateFormat.getValue().toUpperCase();

        if (locationDetails) {
            if (dateFormat === 'MM/DD/YYYY' || dateFormat === 'YYYY/MM/DD') {
                return new Date(locationDetails.lastCollectedDate);
            } else if (dateFormat === 'DD/MM/YYYY') {
                return new Date(
                    String(locationDetails.lastCollectedDate).replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'),
                );
            }
        }
        return '';
    }

    private openDataCollectDialog(
        customerId: number,
        locationId: number,
        locationDetails: LocationDetails,
        lastCollectDate: Date | string,
    ): void {
        const dataCollectionConfig: CollectData = {
            lastCollectDate,
            series: locationDetails.series,
            telemetryEnd: locationDetails.telemetryEnd,
        };
        const dialogRef = this.dialog.open<DataCollectionComponent, CollectData, DataCollectionDialogResult>(
            DataCollectionComponent,
            {
                disableClose: true,
                data: dataCollectionConfig,
            },
        );

        dialogRef.afterClosed().subscribe((response: DataCollectionDialogResult | undefined) => {
            if (!response || !response.success || !response.startDate || !response.endDate) return;

            const start = this.formatCollectDates(response.startDate);
            const end = this.formatCollectDates(response.endDate);

            this.collectLocation(customerId, locationId, start, end);
        });
    }

    private formatCollectDates(date: Date) {
        const isoDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        return isoDate.split('.')[0];
    }

    private collectLocation(customerId: number, locationId: number, start: string, end: string) {
        this.collectService.collectLocation(customerId, locationId, start, end).subscribe(() => {
            this.snackBar.open(
                this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.COLLECT_INIT_SUCCESS_MSG'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
                {
                    duration: 10000,
                },
            );
        }, this.collectionError);
    }

    private collectionError(err: HttpErrorResponse) {
        if (err.status === 400) {
            this.snackBar.open(
                this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.INVALID_IP_COLLECTIONS'),
                this.translateService.instant('COMMON.DISMISS_TEXT'),
                {
                    panelClass: 'custom-error-snack-bar',
                },
            );

            return;
        }

        this.statusCodeService.statusCode409.subscribe((statusResponse: boolean) => {
            if (statusResponse) {
                this.snackBar.open(
                    this.translateService.instant('HOME.MAP.MARKER_LOCATION_DETAIL.COLLECT_INIT_SUCCESS_MSG'),
                    this.translateService.instant('COMMON.DISMISS_TEXT'),
                    {
                        duration: 10000,
                        panelClass: 'custom-error-snack-bar',
                    },
                );
            }
        });
    }

    // schedule collect

    public navigateToScheduleCollectDashboard(customerId: number, locationId: number) {
        const queryParamsItems = { c: customerId };

        if (!locationId) {
            this.router.navigate([scheduleCollectDashboardURL], {
                queryParams: queryParamsItems,
            });
            return;
        }

        const scheduleDialogData: ScheduleDialogData = {
            customerId: customerId,
            locationId: locationId,
            isEditMode: false,
        };

        const scheduleDialogOptions = {
            disableClose: true,
            data: scheduleDialogData,
            autoFocus: false,
        };

        this.scheduleCollectionService.getScheduleByLocationId(customerId, locationId).subscribe(
            (response) => {
                if (response && response.scheduleId) {
                    scheduleDialogData.selectedScheduleId = response.scheduleId;
                    scheduleDialogData.isEditMode = true;
                }

                this.openScheduleDialog(scheduleDialogOptions);
            },
            () => this.openScheduleDialog(scheduleDialogOptions),
        );
    }

    private openScheduleDialog(options: { disableClose: boolean; data: ScheduleDialogData; autoFocus: boolean }) {
        this.dialog.open(AdsPrismAddScheduleComponent, options);
    }

    public exportLocationXml(customerId: number, locationId: number) {
        this.locationService.locationsExport(customerId, locationId).subscribe((result) => {
            if (result) {
                this.snackBar.open(
                    `"${result}" ${this.translateService.instant('COMMON.EXPORTED_SUCCESSFULLY_IN_VAULT')}`,
                    this.translateService.instant('COMMON.DISMISS_TEXT'),
                    {
                        duration: 3000,
                    },
                );
            }
        });
    }
}
